import React from 'react'
import styled from 'styled-components'

import { useApiData } from '@hozana/api/hooks/useApiData'
import { useSelector } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { mediaQuery } from '@hozana/styling/responsive'
import { QA } from '@hozana/tests/constants'

import { Div } from 'elements/layout/Div'
import { Li } from 'elements/layout/Li'
import { ScrollDiv } from 'elements/layout/ScrollDiv'
import { Ul } from 'elements/layout/Ul'
import { Link } from 'elements/text/Link'
import { Label } from 'elements/ui/Label'

import { PAGE } from 'routes/constants'

import type { TCommunity } from 'modules/community/types'
import { getTagsQuery } from 'modules/tag/queries'
import { selectTags } from 'modules/tag/selectors'

const StyledUl = styled(Ul)`
  ${mediaQuery('xs', 'sm')} {
    li:last-child {
      margin-right: 150px;
    }
    white-space: nowrap;
  }
`

type TCommunityTagLinksListProps = {
  community: TCommunity
}

export const CommunityTagLinksList: TFC<TCommunityTagLinksListProps> = ({ community }) => {
  const { locale } = useRouter()

  const [tagsResult, tagsQuery] = useApiData(
    () => ({
      query: getTagsQuery({
        // We pass the lang only if it is different from the current lang
        // If lang is defined, another query key is used to avoid caching problems
        lang: community.lang !== locale ? community.lang : undefined,
      }),
    }),
    [community.lang, locale],
  )

  const tags = useSelector((state) => selectTags(state, tagsResult))

  const communityTagsIds = community.tags

  if (!tags || tagsQuery.loading) return null

  return communityTagsIds && communityTagsIds.length > 0 ? (
    <Div align="middle" h={{ xs: '36px', md: 'auto' }}>
      <ScrollDiv overflowX="auto" overflowY="hidden" flex="1">
        <StyledUl horizontal>
          {communityTagsIds.map((communityTagId, index) => {
            const tag = tags.find((t) => t?.id === communityTagId)
            return tag ? (
              <Li key={index} m="0 5px 0 0">
                <Link
                  to={{
                    pathname: PAGE.COMMUNITY_LIST,
                    ...(tag.slug && { query: { tagSlugs: [tag.slug] } }),
                  }}
                  locale={community.lang}
                  reload
                  data-testid={QA.COMMUNITY_PAGE.DESCRIPTION.TAG}
                >
                  <Label colors="neutral">{tag.name}</Label>
                </Link>
              </Li>
            ) : null
          })}
        </StyledUl>
      </ScrollDiv>
    </Div>
  ) : null
}
